import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types/app';
import { useAuth } from './useAuth';

// Must stay a single string literal — see the note in features/admin/useUsers.ts.
// prettier-ignore
const PROFILE_COLUMNS = 'id, full_name, email, phone, avatar_url, role, team_id, manager_id, is_active, accepts_new_leads, capacity_max, areas, languages, status, last_login_at, created_at, updated_at';

export const profileKeys = {
  all: ['profiles'] as const,
  mine: (userId: string) => ['profiles', 'me', userId] as const,
};

/**
 * The signed-in user's own profile row — the truth source for role in the UI.
 *
 * `profiles_select_self` always permits this read, with no is_active gate, so a
 * deactivated user still gets their row back and the shell can explain why they
 * cannot get in rather than showing an empty screen.
 */
export function useProfile(): UseQueryResult<Profile | null> {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: userId ? profileKeys.mine(userId) : ['profiles', 'me', 'anonymous'],
    enabled: Boolean(userId),
    // A role change should surface quickly; this is one small row.
    staleTime: 60_000,
    queryFn: async (): Promise<Profile | null> => {
      const { data, error } = await supabase
        .from('profiles')
        .select(PROFILE_COLUMNS)
        .eq('id', userId!)
        .maybeSingle();

      if (error) throw error;
      return data ?? null;
    },
  });
}
